import { useState, useRef, useEffect } from 'react';
import { validators, validateFields, ValidationErrors } from '../../../utils/formValidation';
import { API_BASE_URL, API_PATHS } from '../../../config/api';

const FIELD_ORDER = ['inquiry_type', 'name', 'furigana', 'email', 'subject', 'message'] as const;

// ----------------------------------------------------------------
// Modal: 送信中 or 結果表示
// ----------------------------------------------------------------
interface SubmitModalProps {
  phase: 'loading' | 'result' | null;
  result: boolean | null;
  message: string | null;
  errorMessage: string | null;
  onClose: () => void;
}

const SubmitModal = ({ phase, result, message, errorMessage, onClose }: SubmitModalProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (phase !== 'loading') {
      setProgress(0);
      return;
    }
    setProgress(0);
    const steps = [10, 25, 40, 55, 68, 78, 85];
    const timers: ReturnType<typeof setTimeout>[] = [];
    steps.forEach((target, i) => {
      timers.push(setTimeout(() => setProgress(target), i * 420));
    });
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  if (!phase) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 bg-white rounded-2xl shadow-xl w-[90%] max-w-sm mx-auto p-8 flex flex-col items-center gap-6">
        {phase === 'loading' && (
          <div className="w-full text-center">
            <p className="text-sm font-semibold text-gray-700 mb-4">送信中...</p>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#89B56B] to-[#5DAA68] rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-[#89B56B] font-semibold mt-2 text-right">{progress}%</p>
          </div>
        )}

        {phase === 'result' && (
          <>
            <div className="w-full flex flex-col gap-2">
              {message && (
                <div className={`w-full px-4 py-3 rounded-lg text-sm text-center leading-relaxed ${result ? 'bg-[#89B56B]/8 text-gray-700' : 'bg-red-50 text-red-700'}`}>
                  {message}
                </div>
              )}
              {!result && errorMessage && (
                <p className="w-full text-sm text-center text-gray-700 leading-relaxed">
                  {errorMessage}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`w-full py-3 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                result
                  ? 'bg-[#89B56B] text-white hover:bg-[#5DAA68]'
                  : 'bg-red-500 text-white hover:bg-red-600'
              }`}
            >
              閉じる
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------
// メインフォーム
// ----------------------------------------------------------------
const ContactForm = () => {
  const [formData, setFormData] = useState({
    inquiry_type: '',
    name: '',
    furigana: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [modalPhase, setModalPhase] = useState<'loading' | 'result' | null>(null);
  const [apiResult, setApiResult] = useState<boolean | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const buildValidationRules = (data: typeof formData) => [
    { field: 'inquiry_type', value: data.inquiry_type, checks: [(v: string) => validators.required(v, 'お問い合わせ種別', 'お問い合わせ種別を選択してください')] },
    { field: 'name',         value: data.name,         checks: [(v: string) => validators.required(v, '氏名')] },
    { field: 'furigana',     value: data.furigana,     checks: [(v: string) => validators.hiragana(v), (v: string) => validators.optionalMaxLength(v, 20, 'ふりがな')] },
    { field: 'email',        value: data.email,        checks: [(v: string) => validators.emailTrimmed(v), (v: string) => validators.maxLength(v, 40, 'メールアドレス')] },
    { field: 'subject',      value: data.subject,      checks: [] },
    { field: 'message',      value: data.message,      checks: [(v: string) => validators.required(v, 'メッセージ本文'), (v: string) => validators.maxLength(v, 500), (v: string) => validators.containsHiragana(v, 'メッセージ本文')] },
  ];

  const validate = () => {
    const newErrors = validateFields(buildValidationRules(formData));
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let errorMsg = '';
    if (name === 'name') {
      errorMsg = validators.required(value, '氏名');
    } else if (name === 'email') {
      errorMsg = validators.emailTrimmed(value);
      if (!errorMsg) errorMsg = validators.maxLength(value, 40, 'メールアドレス');
    } else if (name === 'message') {
      errorMsg = validators.required(value, 'メッセージ本文');
      if (!errorMsg) errorMsg = validators.containsHiragana(value, 'メッセージ本文');
    }
    if (errorMsg) setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. ハニーポット（ダミー項目）のチェック
    const form = e.currentTarget as HTMLFormElement;
    const honeyPotEl = form.elements.namedItem('website') as HTMLInputElement | null;
    const honeyPotVal = honeyPotEl?.value ?? '';
    if (honeyPotVal) {
      // ボット判定：API通信を行わずに成功風モーダルを表示して処理を無効化する
      setApiResult(null);
      setApiMessage(null);
      setApiErrorMessage(null);
      setModalPhase('loading');
      window.setTimeout(() => {
        setApiResult(true);
        setApiMessage('送信が完了しました。');
        setApiErrorMessage(null);
        setModalPhase('result');
        setFormData({ inquiry_type: '', name: '', furigana: '', email: '', subject: '', message: '' });
        setErrors({});
        if (honeyPotEl) honeyPotEl.value = '';
      }, 1000);
      return;
    }

    // 2. 通常の入力値バリデーション
    if (!validate()) {
      const errs = validateFields(buildValidationRules(formData));
      const firstErrorField = FIELD_ORDER.find((field) => !!errs[field]);
      if (firstErrorField && fieldRefs.current[firstErrorField]) {
        fieldRefs.current[firstErrorField]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setApiResult(null);
    setApiMessage(null);
    setApiErrorMessage(null);
    setModalPhase('loading');

    try {
      const res = await fetch(`${API_BASE_URL}${API_PATHS.contact}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      const result: boolean = data?.result === true;
      const msg: string | null = typeof data?.message === 'string' ? data.message : (result ? '送信が完了しました。' : null);
      const errMsg: string | null = !result && typeof data?.errorMessage === 'string' ? data.errorMessage : null;

      setApiResult(result);
      setApiMessage(msg);
      setApiErrorMessage(errMsg);
      setModalPhase('result');

      if (result) {
        setFormData({ inquiry_type: '', name: '', furigana: '', email: '', subject: '', message: '' });
      }
    } catch {
      setApiResult(false);
      setApiMessage('通信エラーが発生しました。もう一度お試しください。');
      setApiErrorMessage(null);
      setModalPhase('result');
    }
  };

  const handleModalClose = () => {
    setModalPhase(null);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <form
          id="contact-form"
          noValidate
          onSubmit={handleSubmit}
          className="relative bg-[#F8FAF6] rounded-2xl p-8 md:p-12 shadow-[0_2px_16px_rgba(0,0,0,0.03)]"
        >
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="contact-website">Website</label>
            <input type="text" name="website" id="contact-website" tabIndex={-1} autoComplete="off" />
          </div>

          <div className="space-y-6">

            {/* お問い合わせ種別 */}
            <div ref={(el) => { fieldRefs.current['inquiry_type'] = el; }}>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-3">
                お問い合わせ種別
                <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
              </label>
              <div className="flex flex-col gap-2">
                {[
                  { value: '0', label: '協業について' },
                  { value: '1', label: '採用・人材紹介について' },
                  { value: '2', label: 'その他お問い合わせ' },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inquiry_type"
                      value={value}
                      checked={formData.inquiry_type === value}
                      onChange={handleChange}
                      className="w-4 h-4 accent-[#89B56B] cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">{label}</span>
                  </label>
                ))}
              </div>
              {errors['inquiry_type'] && <p className="text-xs text-red-500 mt-2">{errors['inquiry_type']}</p>}
            </div>

            {/* 氏名 */}
            <div ref={(el) => { fieldRefs.current['name'] = el; }}>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                氏名 <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
              </label>
              <input
                type="text" name="name" value={formData.name}
                onChange={handleChange} onBlur={handleBlur} maxLength={20} placeholder="姓　名"
                className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${errors['name'] ? 'border-red-400' : 'border-gray-200'}`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors['name'] ? <p className="text-xs text-red-500">{errors['name']}</p> : <span />}
                <p className="text-xs text-gray-400">{formData.name.length}/20</p>
              </div>
            </div>

            {/* ふりがな */}
            <div ref={(el) => { fieldRefs.current['furigana'] = el; }}>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                ふりがな <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">任意</span>
              </label>
              <input
                type="text" name="furigana" value={formData.furigana}
                onChange={handleChange} maxLength={20} placeholder="せい　めい"
                className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${errors['furigana'] ? 'border-red-400' : 'border-gray-200'}`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors['furigana'] ? <p className="text-xs text-red-500">{errors['furigana']}</p> : <span />}
                <p className="text-xs text-gray-400">{formData.furigana.length}/20</p>
              </div>
            </div>

            {/* メールアドレス */}
            <div ref={(el) => { fieldRefs.current['email'] = el; }}>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                メールアドレス <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
              </label>
              <input
                type="email" name="email" value={formData.email}
                onChange={handleChange} onBlur={handleBlur} maxLength={40} placeholder="example@email.com"
                className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors ${errors.email ? 'border-red-400' : 'border-gray-200'}`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.email ? <p className="text-xs text-red-500">{errors.email}</p> : <span />}
                <p className="text-xs text-gray-400">{formData.email.length}/40</p>
              </div>
            </div>

            {/* 題名 */}
            <div ref={(el) => { fieldRefs.current['subject'] = el; }}>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                題名 <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">任意</span>
              </label>
              <input
                type="text" name="subject" value={formData.subject}
                onChange={handleChange} maxLength={40} placeholder="お問い合わせの件名"
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors"
              />
              <div className="flex items-center justify-end mt-1">
                <p className="text-xs text-gray-400">{formData.subject.length}/40</p>
              </div>
            </div>

            {/* メッセージ本文 */}
            <div ref={(el) => { fieldRefs.current['message'] = el; }}>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 mb-2">
                メッセージ本文 <span className="text-xs text-white bg-[#89B56B] px-2 py-0.5 rounded">必須</span>
              </label>
              <textarea
                name="message" value={formData.message}
                onChange={handleChange} onBlur={handleBlur} rows={8} maxLength={500}
                placeholder="お問い合わせ内容をご記入ください"
                className={`w-full px-4 py-3 bg-white border rounded-lg text-sm focus:outline-none focus:border-[#89B56B] focus:ring-1 focus:ring-[#89B56B]/30 transition-colors resize-vertical ${errors['message'] ? 'border-red-400' : 'border-gray-200'}`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors['message'] ? <p className="text-xs text-red-500">{errors['message']}</p> : <span />}
                <p className="text-xs text-gray-400">{formData.message.length}/500</p>
              </div>
            </div>
          </div>

          {/* 送信ボタン */}
          <div className="mt-10 text-center">
            <button
              type="submit"
              disabled={modalPhase === 'loading'}
              className="inline-flex items-center gap-2 px-10 py-3.5 bg-[#89B56B] text-white text-sm font-medium rounded-full hover:bg-[#5DAA68] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-[#89B56B]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上記の内容で送信する
            </button>
          </div>
        </form>
      </div>

      {/* モーダル */}
      <SubmitModal
        phase={modalPhase}
        result={apiResult}
        message={apiMessage}
        errorMessage={apiErrorMessage}
        onClose={handleModalClose}
      />
    </section>
  );
};

export default ContactForm;
